import { pipe } from "fp-ts/lib/function";
import React, { useState } from "react";
import { CaseMemberReScreenIdCheckForm, CaseMemberRunScreeningIdCheckForm, TCaseMemberCreateIdCheckForm, TCaseMemberFlagNeedsVideoCallForm, TCaseMemberIdCheckForm, TCaseMemberReScreenIdCheckForm, TCaseMemberRunScreeningIdCheckForm } from "../../../../domain/codecs/form/CaseMemberForm";
import { CRMDropdownComponent } from "../CRMDropdownComponent/CRMDropdownComponent";
import { CRMSpacingColumn } from "../CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../CRMSpacingRow/CRMSpacingRow";
import { array, ord } from "fp-ts";
import { Ord } from "fp-ts/lib/Date";
import { Ord as ordNumber } from "fp-ts/number";
import { DateTime } from "luxon";
import { CRMCodecEditForm } from "../CRMCodecEditForm/CRMCodecEditForm";
import { CRMSpacer } from "../CRMSpacer/CRMSpacer";
import { CRMLink } from "../CRMLink/CRMLink";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMPadding } from "../Simple/CRMPadding/CRMPadding";
import { TextCenter } from "../TextCenter/TextCenter";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";
import { CRMFormButtonIconWithLabel } from "../CRMFormButtonIconWithLabel/CRMFormButtonIconWithLabel";
import { CRMButtonIcon } from "../CRMButtonIcon/CRMButtonIcon";
import { IdentityDocumentEstablished, TUsersIdCheckComplyAdvantageMatch, TUsersLegalDetailsAuditLogEntry, UsersIdCheck1, UsersIdCheck2, UsersIdCheck5, UsersIdCheckAddress, UsersIdCheckManualPass } from "../../../../domain/codecs/UsersIdCheck";
import { CRMAutosaveIndicatorWrap } from "../CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { FormStatus_highestPriority } from "../../../../shared/src/codecs/codec";
import { CRMFormButtonIcon } from "../CRMFormButtonIcon/CRMFormButtonIcon";
import { CRMCaseMemberIdChecksImageComponent } from "../CRMCaseMemberIdChecksImageComponent/CRMCaseMemberIdChecksImageComponent";
import { CRMTitleSubSection } from "../CRMTitleSubSection/CRMTitleSubSection";
import { CRMNoticeBoxComponent } from "../CRMNoticeBoxComponent/CRMNoticeBoxComponent";
import { CRMPill } from "../Simple/CRMPill/CRMPill";
import { CRMCardOutsideMinor } from "../CRMCardOutsideMinor/CRMCardOutsideMinor";
import { WeightSemiBold } from "../WeightSemiBold/WeightSemiBold";
import { TextColor } from "../BuildingBlocks/TextColor";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { TCRMMainColours } from "../../models/TCRMMainColours";
import { CRMCardOutsidePopupBasic } from "../CRMCardOutsidePopupBasic/CRMCardOutsidePopupBasic";
import CRMTextAreaComponent from "../CRMTextAreaComponent/CRMTextAreaComponent";
import CRMInputLabelAndErrorWrapComponent from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";

type TPepMatchBadge = { label: string, colour: TCRMMainColours };

const matchCategoryBadges = (amlTypes: Array<string>): Array<TPepMatchBadge> => {
    const badges: Array<TPepMatchBadge> = [];
    if (amlTypes.includes("sanction")) {
        badges.push({ label: "Sanction", colour: "highlights-instructional-brick-10" });
    }
    if (amlTypes.some((t) => t.startsWith("pep"))) {
        badges.push({ label: "PEP", colour: "highlights-pertinent-yellow-10" });
    }
    if (amlTypes.includes("warning")) {
        badges.push({ label: "Adverse media", colour: "neutral-8" });
    }
    if (amlTypes.includes("fitness_probity")) {
        badges.push({ label: "Fitness & probity", colour: "neutral-8" });
    }
    return badges;
};

const matchTypeLabels: Record<string, string> = {
    name_exact: "exact name",
    aka_exact: "alias name",
    name_fuzzy: "similar name",
    aka_fuzzy: "similar alias",
    phonetic_name: "name that sounds alike",
    equivalent_name: "equivalent name",
    year_of_birth: "year of birth",
    date_of_birth: "date of birth",
};

const humanJoin = (xs: Array<string>): string =>
    xs.length <= 1
        ? (xs[0] || "")
        : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;

const matchReasonLine = (matchTypes: Array<string>): string => {
    const labels = matchTypes.map((t) => matchTypeLabels[t] || t.replace(/_/g, " "));
    return labels.length > 0 ? `Matched on ${humanJoin(labels)}` : "";
};

const matchMetaLine = (m: TUsersIdCheckComplyAdvantageMatch): string =>
    [
        m.date_of_birth ? `Born ${m.date_of_birth}` : "",
        m.country_codes.length > 0 ? m.country_codes.join(", ") : "",
        m.match_score !== null ? `Match score ${m.match_score}` : "",
    ]
        .filter((s) => s.length > 0)
        .join(" · ");

type TProps = {
    idChecks: Array<TCaseMemberIdCheckForm>;
    newIdCheckForm: TCaseMemberCreateIdCheckForm;
    flagNeedsVideoCallForm: TCaseMemberFlagNeedsVideoCallForm;
    needsVideoCallFlagged: boolean;
    onNew: (p: TCaseMemberCreateIdCheckForm) => void;
    onChange: (idCheck: TCaseMemberIdCheckForm) => void;
    onReScreen: (p: TCaseMemberReScreenIdCheckForm) => void;
    onRunScreening: (p: TCaseMemberRunScreeningIdCheckForm) => void;
    onFlagNeedsVideoCall: (p: TCaseMemberFlagNeedsVideoCallForm) => void;
};

const missingScreeningFields = (children: TCaseMemberIdCheckForm["children"]): Array<string> => [
    ...(children.has_legal_name ? [] : ["legal name"]),
    ...(children.has_legal_dob ? [] : ["date of birth"]),
    ...(children.has_correspondence_address ? [] : ["address"]),
];

const idCheckFailReasonCopy: Record<string, string> = {
    user_abandoned_verification: "The client didn't finish the verification",
    consent_declined: "The client declined consent",
    unsupported_id_country: "The ID is from an unsupported country",
    device_not_supported: "The client's device isn't supported",
    document_expiry: "The ID document had expired",
    unsupported_id_type: "The ID document type isn't supported",
    fraud: "The document couldn't be verified (possible fraud)",
    selfie_failed: "The selfie didn't match the document (possible fraud)",
    under_supported_age: "The client is under the supported age",
    name_does_not_match: "The name didn't match our records",
    date_of_birth_does_not_match: "The date of birth didn't match our records",
};

const legalFieldLabels: Record<string, string> = {
    legal_first_legal_name: "First name",
    legal_middle_legal_name: "Middle name",
    legal_last_legal_name: "Last name",
    legal_date_of_birth: "Date of birth",
    legal_correspondence_building_name: "Building name",
    legal_correspondence_building_number: "Building number",
    legal_correspondence_sub_building_name: "Flat / Sub-building name",
    legal_correspondence_sub_building_number: "Flat / Sub-building number",
    legal_correspondence_street_name: "Street",
    legal_correspondence_city_town: "Town / City",
    legal_correspondence_locality: "Locality",
    legal_correspondence_district: "District",
    legal_correspondence_county: "County",
    legal_correspondence_country: "Country",
    legal_correspondence_postcode: "Postcode",
};

const formatLegalAuditActor = (entry: TUsersLegalDetailsAuditLogEntry): string => {
    if (entry.changed_by_user_type === "client") {
        return "client";
    }
    if (entry.changed_by_user_name) {
        return `staff (${entry.changed_by_user_name})`;
    }
    return "staff";
};

export const CRMCaseMemberIdChecksComponent = (props: React.PropsWithChildren<TProps>): JSX.Element => {
    const [formIdSelected, setFormIdSelected] = useState<string>("");
    const [needsVideoCallPopupOpen, setNeedsVideoCallPopupOpen] = useState<boolean>(false);
    const [needsVideoCallReason, setNeedsVideoCallReason] = useState<string>("");

    const getIdChecksSorted = () =>
        pipe(
            props.idChecks,
            array.sortBy([ord.contramap((idCheckForm: TCaseMemberIdCheckForm) => DateTime.fromISO(idCheckForm.children.created_at).toJSDate())(Ord)]),
            array.reverse,
        );

    const getSelectedIdCheckId = () =>
        (
            formIdSelected
            && pipe(
                getIdChecksSorted(),
                array.map((form) => form.edited.id)
            ).includes(formIdSelected)
        )
            ? formIdSelected
            : getIdChecksSorted()[0].edited.id;

    const closeNeedsVideoCallPopup = () => {
        setNeedsVideoCallPopupOpen(false);
        setNeedsVideoCallReason("");
    };

    return (<>
        {props.idChecks.length > 0
            ? <CRMAutosaveIndicatorWrap status={
                pipe(
                    props.idChecks,
                    array.map((form) => form.status),
                    FormStatus_highestPriority,
                )
            }>
                <CRMSpacingColumn spacing="medium">
                    <CRMSpacingRow childSize="2fr 1fr">
                        <CRMDropdownComponent
                            options={pipe(
                                getIdChecksSorted(),
                                array.mapWithIndex((i, idCheckForm) => ({
                                    value: idCheckForm.edited.id,
                                    label: `ID check #${props.idChecks.length - i} - ${DateTime.fromISO(idCheckForm.children.created_at).setZone("Europe/London").toFormat("DD")}${idCheckForm.children.potential_fraud && idCheckForm.edited.potential_fraud_outcome === "unknown" ? " (Fraud risk)" : ""}`,
                                }))
                            )}
                            value={getSelectedIdCheckId()}
                            displayError={false}
                            onChange={setFormIdSelected}
                        />

                        <div>
                            <CRMFormButtonIcon
                                formStatus={
                                    (props.newIdCheckForm.status === "twoFactorRequired" || props.newIdCheckForm.status === "notFound" || props.newIdCheckForm.status === "unauthorised") ? "failure"
                                        : props.newIdCheckForm.status === "untouched" ? "requiresSubmission"
                                            : props.newIdCheckForm.status
                                }
                                ButtonElement={(p) => <CRMButtonIcon variant="primary" {...p} />}
                                icon="add"
                                onClick={() => {
                                    setFormIdSelected("");
                                    props.onNew(props.newIdCheckForm);
                                }}
                            />
                        </div>
                    </CRMSpacingRow>

                    <div>
                        {pipe(
                            props.idChecks,
                            array.mapWithIndex((i, form) =>
                                <div key={i}>
                                    {form.edited.id === getSelectedIdCheckId() && <>
                                        <CRMSpacingColumn spacing="medium">
                                            {form.children.potential_fraud && form.edited.potential_fraud_outcome !== "not_fraud" && <CRMNoticeBoxComponent>
                                                Stripe has reported this ID check may have used a fraudulent document or the person using the document either didn't
                                                look the same as the person shown in the document's picture or they used a recording of the person.
                                            </CRMNoticeBoxComponent>}

                                            {form.children.has_expired
                                                && IdentityDocumentEstablished.includes(form.edited.id_check_result_identity_document_status)
                                                && form.edited.id_check_result_address_status === "pass"
                                                && form.edited.id_check_result_peps_sanctions_status === "pass"
                                                && <CRMNoticeBoxComponent>
                                                    This ID check has expired.
                                                </CRMNoticeBoxComponent>}

                                            <CRMCodecEditForm
                                                codec={UsersIdCheck1}
                                                model={form.edited}
                                                validationErrors={form.validationErrors}
                                                onChange={(p) =>
                                                    props.onChange({
                                                        ...form,
                                                        edited: {
                                                            ...form.edited,
                                                            ...p,
                                                        }
                                                    })
                                                }
                                            />

                                            {/* Why the identity check failed */}
                                            {form.edited.id_check_result_identity_document_status === "fail"
                                                && form.children.identity_document_fail_reasons.length > 0
                                                && <CRMNoticeBoxComponent>
                                                    <CRMParagraph>
                                                        <WeightBold>Why this failed:</WeightBold>
                                                    </CRMParagraph>
                                                    <ul>
                                                        {Array.from(new Set(form.children.identity_document_fail_reasons)).map((reason, ri) =>
                                                            <li key={ri}>{idCheckFailReasonCopy[reason] || reason}</li>
                                                        )}
                                                    </ul>
                                                </CRMNoticeBoxComponent>}

                                            {/* Legal details audit trail */}
                                            {form.children.legal_details_audit && form.children.legal_details_audit.length > 0 && (
                                                <CRMNoticeBoxComponent>
                                                    <CRMParagraph>
                                                        <WeightBold>Legal details updated:</WeightBold>
                                                    </CRMParagraph>
                                                    <ul>
                                                        {form.children.legal_details_audit.map((entry) => (
                                                            <li key={entry.id}>
                                                                <WeightBold>{legalFieldLabels[entry.field_name] || entry.field_name}</WeightBold> changed from{" "}
                                                                {entry.old_value ? `"${entry.old_value}"` : <em>(empty)</em>} to{" "}
                                                                {entry.new_value ? `"${entry.new_value}"` : <em>(empty)</em>}{" "}
                                                                by {formatLegalAuditActor(entry)} on {DateTime.fromISO(entry.created_at).setZone("Europe/London").toFormat("dd MMM yyyy, HH:mm")}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </CRMNoticeBoxComponent>
                                            )}

                                            {/* Manual pass — reason is required and only sign-off users can save it */}
                                            {form.edited.id_check_result_identity_document_status === "manual_pass" && <>
                                                {form.children.potential_fraud && form.edited.potential_fraud_outcome !== "not_fraud"
                                                    && <CRMNoticeBoxComponent>
                                                        Manual pass is unavailable until the fraud review is resolved.
                                                    </CRMNoticeBoxComponent>}
                                                <CRMCodecEditForm
                                                    codec={UsersIdCheckManualPass}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(p) =>
                                                        props.onChange({
                                                            ...form,
                                                            edited: {
                                                                ...form.edited,
                                                                ...p,
                                                            }
                                                        })
                                                    }
                                                />
                                            </>}

                                            {/* Re-screen rows carry identity forward and have no images of their own */}
                                            {form.children.rescreened_from_id_check_id !== null
                                                && <CRMNoticeBoxComponent>
                                                    Identity was carried forward from an earlier check — only the screening (PEPs/sanctions and address) was refreshed.
                                                </CRMNoticeBoxComponent>}

                                            {/* ID Images */}
                                            {form.children.id_check_image_urls.length > 0 && <CRMSpacingRow spacing="medium" justifyContent="start">
                                                {pipe(
                                                    form.children.id_check_image_urls,
                                                    array.mapWithIndex((ii, imageUrl) =>
                                                        <CRMCaseMemberIdChecksImageComponent key={ii} imageUrl={imageUrl} />
                                                    ),
                                                )}
                                            </CRMSpacingRow>}

                                            <div>
                                                <CRMCodecEditForm
                                                    codec={UsersIdCheckAddress}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(p) =>
                                                        props.onChange({
                                                            ...form,
                                                            edited: {
                                                                ...form.edited,
                                                                ...p,
                                                            }
                                                        })
                                                    }
                                                />
                                                {/* The residency check is fire-and-forget, so the address sits at "unknown"
                                                    until Melissa lands. Show it as pending so staff know it's running, not stuck. */}
                                                {form.edited.id_check_result_address_status === "unknown"
                                                    && form.children.id_check_awaiting_third_party_responses && <>
                                                        <CRMSpacer size="tiny" />
                                                        <CRMPill colour="highlights-pertinent-yellow-10" mode="stacked">
                                                            <WeightSemiBold>Pending — running residency check, refresh shortly</WeightSemiBold>
                                                        </CRMPill>
                                                    </>}
                                                {form.children.uploaded_proof_of_address_url && <>
                                                    <CRMSpacer size="tiny" />
                                                    <CRMLink
                                                        href={form.children.uploaded_proof_of_address_url}
                                                        linkStyle="normal"
                                                        target="_blank"
                                                    >
                                                        <WeightBold>Open uploaded proof of address</WeightBold>
                                                    </CRMLink>
                                                </>}
                                            </div>

                                            <CRMCodecEditForm
                                                codec={UsersIdCheck2}
                                                model={form.edited}
                                                validationErrors={form.validationErrors}
                                                onChange={(p) =>
                                                    props.onChange({
                                                        ...form,
                                                        edited: {
                                                            ...form.edited,
                                                            ...p,
                                                        }
                                                    })
                                                }
                                            />

                                            {form.edited.id_check_result_peps_sanctions_status === "fail"
                                                && form.children.pep_declaration_signed_at !== null
                                                && <CRMNoticeBoxComponent>
                                                    This client confirmed that one of the PEP/sanctions matches below is them. Please ask your manager to review before proceeding.
                                                </CRMNoticeBoxComponent>}

                                            {form.children.comply_advantage_matches.length > 0 && <CRMSpacingColumn spacing="medium">
                                                <CRMTitleSubSection>
                                                    Comply Advantage matches ({form.children.comply_advantage_matches.length})
                                                </CRMTitleSubSection>
                                                <CRMSpacingColumn spacing="tiny">
                                                    {pipe(
                                                        form.children.comply_advantage_matches,
                                                        array.sortBy([ord.contramap((m: TUsersIdCheckComplyAdvantageMatch) => m.aml_types.includes("sanction") ? 0 : 1)(ordNumber)]),
                                                        array.mapWithIndex((mi, m) => {
                                                            const matchDetails =
                                                                <CRMSpacingColumn spacing="tiny">
                                                                    <CRMSpacingRow spacing="tiny" alignItems="center">
                                                                        <CRMParagraph>
                                                                            <WeightBold>{m.matching_name || "Unnamed match"}</WeightBold>
                                                                        </CRMParagraph>
                                                                        {pipe(
                                                                            matchCategoryBadges(m.aml_types),
                                                                            array.mapWithIndex((bi, badge) =>
                                                                                <CRMPill key={bi} colour={badge.colour} mode="stacked">
                                                                                    <WeightSemiBold>{badge.label}</WeightSemiBold>
                                                                                </CRMPill>
                                                                            ),
                                                                        )}
                                                                    </CRMSpacingRow>
                                                                    {m.political_positions.length > 0 &&
                                                                        <CRMParagraph fontSize={CRMFontSizes.X_SMALL}>
                                                                            <WeightSemiBold>{m.political_positions.join(" · ")}</WeightSemiBold>
                                                                        </CRMParagraph>
                                                                    }
                                                                    {matchMetaLine(m).length > 0 &&
                                                                        <CRMParagraph fontSize={CRMFontSizes.X_SMALL}>
                                                                            <TextColor color={CRMColors.NEUTRAL_2}>{matchMetaLine(m)}</TextColor>
                                                                        </CRMParagraph>
                                                                    }
                                                                    {matchReasonLine(m.match_types).length > 0 &&
                                                                        <CRMParagraph fontSize={CRMFontSizes.X_SMALL}>
                                                                            <TextColor color={CRMColors.NEUTRAL_2}>{matchReasonLine(m.match_types)}</TextColor>
                                                                        </CRMParagraph>
                                                                    }
                                                                </CRMSpacingColumn>;

                                                            return (
                                                                <CRMCardOutsideMinor key={mi} thickLeftBorder={true}>
                                                                    <CRMPadding size="medium">
                                                                        {m.image_url !== null
                                                                            ? <CRMSpacingRow spacing="medium" alignItems="flex-start" childSize="auto 1fr">
                                                                                <CRMCaseMemberIdChecksImageComponent imageUrl={m.image_url} />
                                                                                {matchDetails}
                                                                            </CRMSpacingRow>
                                                                            : matchDetails
                                                                        }
                                                                    </CRMPadding>
                                                                </CRMCardOutsideMinor>
                                                            );
                                                        }),
                                                    )}
                                                </CRMSpacingColumn>
                                            </CRMSpacingColumn>}

                                            {form.children.potential_fraud && <>
                                                <CRMTitleSubSection>
                                                    Fraud review
                                                </CRMTitleSubSection>
                                                <CRMCodecEditForm
                                                    codec={UsersIdCheck5}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(p) =>
                                                        props.onChange({
                                                            ...form,
                                                            edited: {
                                                                ...form.edited,
                                                                ...p,
                                                            }
                                                        })
                                                    }
                                                />
                                            </>}
                                        </CRMSpacingColumn>
                                        {form.children.id_check_result_peps_sanctions_report && <>
                                            <CRMSpacer size="tiny" />
                                            <CRMLink
                                                href={form.children.id_check_result_peps_sanctions_report}
                                                linkStyle="normal"
                                                target="_blank"
                                            >
                                                <WeightBold>Open report to investigate</WeightBold>
                                            </CRMLink>
                                        </>}

                                        {/* Re-screen: re-run screening, carrying this proven identity forward onto a fresh check */}
                                        {(form.edited.id_check_result_identity_document_status === "pass"
                                            || form.edited.id_check_result_identity_document_status === "manual_pass") && <>
                                            {form.children.document_expiry_date !== null && form.children.document_expiry_date <= (DateTime.local().toISODate() || "") ? <>
                                                <CRMSpacer size="medium" />
                                                <CRMNoticeBoxComponent>
                                                    The ID document expired on {DateTime.fromISO(form.children.document_expiry_date).toFormat("d LLL yyyy")}. Re-screen cannot carry an expired document forward into a clean pass. Request a fresh check from the client or, if an exception is justified by policy, record a Manual pass with MLRO sign-off and a reason.
                                                </CRMNoticeBoxComponent>
                                            </> : <>
                                                {form.children.document_expiry_date === null && <>
                                                    <CRMSpacer size="medium" />
                                                    <CRMNoticeBoxComponent>
                                                        Document expiry date is not recorded for this check. Verify the document is still in date before re-screening.
                                                    </CRMNoticeBoxComponent>
                                                </>}
                                                <CRMSpacer size="medium" />
                                                <CRMFormButtonIconWithLabel
                                                    formStatus="requiresSubmission"
                                                    ButtonElement={(p) => <CRMButtonIcon variant="primary" {...p} />}
                                                    icon="repeat"
                                                    label="Re-screen"
                                                    onClick={() => props.onReScreen({
                                                        ...CaseMemberReScreenIdCheckForm.newDefault(),
                                                        edited: {
                                                            ...CaseMemberReScreenIdCheckForm.newDefault().edited,
                                                            id_check_id: form.edited.id,
                                                        },
                                                    })}
                                                />
                                            </>}
                                        </>}

                                        {/* Run screening: screen an unverified check in place (no Stripe pass) so an
                                            authoriser can review the PEP/residency results before recording a manual pass.
                                            Hidden once screening has run (peps no longer unknown) — the one-shot guard. */}
                                        {form.edited.id_check_result_identity_document_status !== "pass"
                                            && form.edited.id_check_result_identity_document_status !== "manual_pass"
                                            && form.edited.id_check_result_peps_sanctions_status === "unknown" && <>
                                                <CRMSpacer size="medium" />
                                                {missingScreeningFields(form.children).length > 0 && <>
                                                    <CRMNoticeBoxComponent>
                                                        Add this client's {humanJoin(missingScreeningFields(form.children))} before running screening.
                                                    </CRMNoticeBoxComponent>
                                                    <CRMSpacer size="tiny" />
                                                </>}
                                                <CRMFormButtonIconWithLabel
                                                    formStatus={missingScreeningFields(form.children).length > 0 ? "untouched" : "requiresSubmission"}
                                                    ButtonElement={(p) => <CRMButtonIcon variant="primary" {...p} />}
                                                    icon="search-list"
                                                    label="Run screening"
                                                    onClick={() => props.onRunScreening({
                                                        ...CaseMemberRunScreeningIdCheckForm.newDefault(),
                                                        edited: {
                                                            ...CaseMemberRunScreeningIdCheckForm.newDefault().edited,
                                                            id_check_id: form.edited.id,
                                                        },
                                                    })}
                                                />
                                            </>}
                                    </>}
                                </div>
                            ),
                        )}
                    </div>
                </CRMSpacingColumn>
            </CRMAutosaveIndicatorWrap>
            : <CRMSpacingRow
                spacing="large"
                alignItems="flex-start"
                childSize="even-fill"
            >
                <CRMPadding size="tiny">
                    <TextCenter>
                        <CRMParagraph>
                            Add a new <WeightBold>ID check</WeightBold> to get started.
                        </CRMParagraph>
                    </TextCenter>
                </CRMPadding>

                <div>
                    <CRMFormButtonIconWithLabel
                        formStatus={
                            (props.newIdCheckForm.status === "twoFactorRequired" || props.newIdCheckForm.status === "notFound" || props.newIdCheckForm.status === "unauthorised") ? "failure"
                                : props.newIdCheckForm.status === "untouched" ? "requiresSubmission"
                                    : props.newIdCheckForm.status
                        }
                        ButtonElement={(p) => <CRMButtonIcon variant="primary" {...p} />}
                        icon="add"
                        label="New ID Check"
                        onClick={() => {
                            setFormIdSelected("");
                            props.onNew(props.newIdCheckForm);
                        }}
                    />
                </div>
            </CRMSpacingRow>
        }

        {/* NEEDS VIDEO CALL — flags this one person so they can book a video call.
            Writes the same needs_video_call outcome a case handler logs on a phone
            call, so the Verification calls section shows it too. */}
        {props.needsVideoCallFlagged
            ? <CRMPill colour="highlights-pertinent-yellow-10" mode="stacked">
                <WeightSemiBold>Needs a video call — they can book one from their portal</WeightSemiBold>
            </CRMPill>
            : <CRMFormButtonIconWithLabel
                formStatus="requiresSubmission"
                ButtonElement={(p) => <CRMButtonIcon variant="primary" {...p} />}
                icon="ondemand-video"
                label="Needs video call"
                onClick={() => setNeedsVideoCallPopupOpen(true)}
            />}

        {needsVideoCallPopupOpen && <CRMCardOutsidePopupBasic
            isOpen={true}
            title="Ask for a video call?"
            context="important"
            onClose={closeNeedsVideoCallPopup}
            closeText="Cancel"
            onCTA={() => {
                props.onFlagNeedsVideoCall({
                    ...props.flagNeedsVideoCallForm,
                    edited: {
                        ...props.flagNeedsVideoCallForm.edited,
                        reason: needsVideoCallReason,
                    },
                });
                closeNeedsVideoCallPopup();
            }}
            ctaText="Yes, ask for a video call"
        >
            <CRMParagraph>
                This person will be able to book a video call with us from their portal. Nobody else on the case is affected.
            </CRMParagraph>
            <CRMSpacer size="medium" />
            <CRMInputLabelAndErrorWrapComponent label="Why do we need one? (optional)">
                <CRMTextAreaComponent
                    value={needsVideoCallReason}
                    placeholder="What you want the call to confirm"
                    onChange={setNeedsVideoCallReason}
                />
            </CRMInputLabelAndErrorWrapComponent>
        </CRMCardOutsidePopupBasic>}
    </>);
};
